/** 유료 플랜 월 정액(원) */
export const PAID_PLAN_MONTHLY_WON = 3000;

/** 달 전체가 아닌 구간만 청구할 때 1일당 금액(원) */
export const PAID_PLAN_PARTIAL_DAY_RATE_WON = 100;

export interface PaidPlanMonthLine {
  /** 예: 2026년 5월 */
  label: string;
  /** 해당 달에서 청구 구간에 포함된 일수 */
  daysBilled: number;
  daysInMonth: number;
  amountWon: number;
}

export interface PaidPlanPricingResult {
  /** 실제 결제(구매)일 — 환불 창 등에 사용 */
  purchaseDate: Date;
  /** 이용(과금) 시작일: 선택 월이 결제 월과 같으면 결제일, 아니면 그 달 1일 — 추가 구매 시에는 남는 구간의 첫 과금일로 조정될 수 있음 */
  periodStart: Date;
  /** 이용 종료일(포함): 시작 연도 + 1년의 2월 말일 */
  periodEndInclusive: Date;
  /**
   * `lines`에 실제 청구된 마지막 날(포함). 부분 월·이용권 기간 상한으로 끊긴 경우 `periodEndInclusive`보다 앞설 수 있음.
   * 이력의 이용 기간 종료일로 저장할 때 이 값을 씁니다.
   */
  billedPeriodEndInclusive: Date;
  lines: PaidPlanMonthLine[];
  totalWon: number;
  /**
   * `computePaidPlanPricingExcludingIndividualCoverage` 전용.
   * 사용자가 선택한 «유료 플랜 시작 월»의 달력 1일(셀렉트 value·라벨용).
   */
  billingAnchorFirstDay?: Date;
}

function atLocalMidnight(y: number, m: number, d: number): Date {
  return new Date(y, m, d, 0, 0, 0, 0);
}

/** 실제 과금 시작일 기준 «다음 해» 2월의 마지막 날(포함 종료일) */
export function getPaidPlanPeriodEndForBillingStart(periodStartFirstDay: Date): Date {
  const y = periodStartFirstDay.getFullYear();
  return new Date(y + 1, 2, 0);
}

/**
 * @deprecated `getPaidPlanPeriodEndForBillingStart` 사용. 구매일 당월 1일 기준 종료일.
 */
export function getPaidPlanPeriodEndInclusive(purchaseDate: Date): Date {
  const start = atLocalMidnight(purchaseDate.getFullYear(), purchaseDate.getMonth(), 1);
  return getPaidPlanPeriodEndForBillingStart(start);
}

function monthLabel(y: number, m: number): string {
  return new Date(y, m, 15).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
}

/** 날짜만 기준으로 양 끝 포함 일수 */
export function daysInclusiveDateOnly(start: Date, end: Date): number {
  const t0 = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const t1 = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((t1 - t0) / 86400000) + 1;
}

export function maxDateOnly(a: Date, b: Date): Date {
  const da = atLocalMidnight(a.getFullYear(), a.getMonth(), a.getDate());
  const db = atLocalMidnight(b.getFullYear(), b.getMonth(), b.getDate());
  return da.getTime() >= db.getTime() ? da : db;
}

function minDateOnly(a: Date, b: Date): Date {
  const da = atLocalMidnight(a.getFullYear(), a.getMonth(), a.getDate());
  const db = atLocalMidnight(b.getFullYear(), b.getMonth(), b.getDate());
  return da.getTime() <= db.getTime() ? da : db;
}

/**
 * 개별 이용권 «이용권 기간»이 시작하는 연도(2월 1일 기준).
 * - 2~12월: 해당 연도의 2월 1일이 기간 시작
 * - 1월: 직전 연도 2월 1일이 시작인 기간에 속함
 */
export function getIndividualPassLicenseWindowStartYear(today: Date): number {
  const y = today.getFullYear();
  const m = today.getMonth();
  if (m >= 1) return y;
  return y - 1;
}

/**
 * 오늘 기준 개별 이용권 1회에 해당하는 이용 기간(날짜만): **2월 1일 ~ 다음 해 2월 말일** (포함).
 */
export function getIndividualPassLicenseWindow(today: Date): { start: Date; end: Date } {
  const ys = getIndividualPassLicenseWindowStartYear(today);
  return {
    start: atLocalMidnight(ys, 1, 1),
    end: atLocalMidnight(ys + 1, 2, 0),
  };
}

/**
 * «Y학년도» 이용권 1회 기간(날짜만): **Y년 2월 1일 ~ (Y+1)년 2월 말일**(포함).
 * UI에서 달력 연도별로 나란히 노출할 때 사용합니다.
 */
export function getSchoolYearPassLicenseWindow(schoolYearLabel: number): { start: Date; end: Date } {
  return {
    start: atLocalMidnight(schoolYearLabel, 1, 1),
    end: atLocalMidnight(schoolYearLabel + 1, 2, 0),
  };
}

/** Y학년도 개별 이용권을 구매·활성화할 수 있는 시점: **Y년 1월 1일**(로컬 자정) 이후 */
export function getSchoolYearPassPurchaseOpensAt(schoolYearLabel: number): Date {
  return atLocalMidnight(schoolYearLabel, 0, 1);
}

/**
 * 고정 이용권 기간(시작·종료) 안에서, **오늘이 속한 달 1일**(단, 윈도우 시작 이후)부터 끝까지의 과금 시작 월 후보.
 */
export function listBillingStartMonthsInLicenseWindow(
  today: Date,
  windowStart: Date,
  windowEnd: Date
): Date[] {
  const ws = atLocalMidnight(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
  const we = atLocalMidnight(windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate());
  const lower = maxDateOnly(atLocalMidnight(today.getFullYear(), today.getMonth(), 1), ws);
  const out: Date[] = [];
  let cy = lower.getFullYear();
  let cm = lower.getMonth();
  while (true) {
    const monthFirst = atLocalMidnight(cy, cm, 1);
    if (monthFirst.getTime() > we.getTime()) break;
    out.push(monthFirst);
    cm += 1;
    if (cm > 11) {
      cm = 0;
      cy += 1;
    }
  }
  return out;
}

/**
 * 개별 이용권에서 선택 가능한 «유료 플랜 시작 월» 후보(각 달 1일).
 * 이용권 기간(2월~다음 해 2월) 안에 있으면서, **결제일이 속한 달 1일 이후**(포함)인 달만 노출합니다.
 */
export function listIndividualLicenseBillingStartMonths(today: Date = new Date()): Date[] {
  const { start: ws, end: we } = getIndividualPassLicenseWindow(today);
  return listBillingStartMonthsInLicenseWindow(today, ws, we);
}

/**
 * 오늘 기준 선택 가능한 «유료 플랜 시작 월»의 각 달 1일.
 * 이번 달 ~ 내년 2월까지(포함). (그룹 이용권 등에 사용)
 */
export function listSelectableBillingStartMonths(today: Date = new Date()): Date[] {
  const ty = today.getFullYear();
  const tm = today.getMonth();
  const lastY = ty + 1;
  const lastM = 1; // 2월
  const out: Date[] = [];
  let y = ty;
  let m = tm;
  while (y < lastY || (y === lastY && m <= lastM)) {
    out.push(atLocalMidnight(y, m, 1));
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}

export function billingStartMonthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 로컬 달력 기준 `YYYY-MM-DD` (날짜 입력·표시용) */
export function toLocalIsoDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` → 해당 일의 로컬 자정. 잘못된 달·일이면 null */
export function parseLocalIsoDateOnly(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da)) return null;
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;
  const d = new Date(y, mo - 1, da, 0, 0, 0, 0);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== da) return null;
  return d;
}

/**
 * 그룹 이용권 등 `input[type=date]` 선택 범위(로컬).
 * 하한: 결제 기준일(오늘), 상한: 해당 학년도 개별 이용권 종료일(`periodEndCapInclusive`).
 * 상한 미지정 시 (오늘 연도+1)년 2월 말일.
 */
export function getGroupPassBillingDatePickBounds(
  purchaseDate: Date = new Date(),
  periodEndCapInclusive?: Date | null
): {
  minIso: string;
  maxIso: string;
} {
  const p = atLocalMidnight(
    purchaseDate.getFullYear(),
    purchaseDate.getMonth(),
    purchaseDate.getDate()
  );
  let max: Date;
  if (periodEndCapInclusive) {
    max = atLocalMidnight(
      periodEndCapInclusive.getFullYear(),
      periodEndCapInclusive.getMonth(),
      periodEndCapInclusive.getDate()
    );
  } else {
    const feb = new Date(purchaseDate.getFullYear() + 1, 2, 0);
    max = atLocalMidnight(feb.getFullYear(), feb.getMonth(), feb.getDate());
  }
  if (max.getTime() < p.getTime()) {
    max = p;
  }
  return { minIso: toLocalIsoDateOnly(p), maxIso: toLocalIsoDateOnly(max) };
}

/**
 * 선택한 시작 월부터 종료일까지 청구.
 * - **선택 월 = 결제 월**이면 과금 시작은 **결제일(당일)**부터, 아니면 **그 달 1일**부터.
 * - 해당 달을 **끝까지** 쓰는 경우: 월 정액 `monthlyWon`.
 * - 달의 일부만 쓰는 경우: 청구 일수 × `partialDayRateWon`(기본 100원), 단 **월 정액을 넘지 않음**.
 * @param billingStartFirstDay 선택한 시작 월(달력 1일 권장). `explicitCalendarStart`가 있으면 앵커 월만 참고.
 * @param purchaseDate 결제일(기본 오늘)
 * @param explicitCalendarStart 그룹 이용권 등: 이용·과금 **시작일**을 연·월·일로 지정할 때. 지정 시 실제 시작은 **선택일과 결제일 중 늦은 날**(날짜만).
 */
export function computePaidPlanPricing(
  billingStartFirstDay: Date,
  purchaseDate: Date = new Date(),
  monthlyWon: number = PAID_PLAN_MONTHLY_WON,
  partialDayRateWon: number = PAID_PLAN_PARTIAL_DAY_RATE_WON,
  /** 지정 시 과금 종료일을 이 날짜(포함) 이하로 자름 — 개별 이용권 2월~다음 해 2월 한도 */
  periodEndCapInclusive?: Date | null,
  explicitCalendarStart?: Date | null
): PaidPlanPricingResult {
  const purchase = atLocalMidnight(
    purchaseDate.getFullYear(),
    purchaseDate.getMonth(),
    purchaseDate.getDate()
  );

  let periodStart: Date;
  let billingAnchorFirstDay: Date;

  if (explicitCalendarStart) {
    const chosen = atLocalMidnight(
      explicitCalendarStart.getFullYear(),
      explicitCalendarStart.getMonth(),
      explicitCalendarStart.getDate()
    );
    periodStart = maxDateOnly(chosen, purchase);
    billingAnchorFirstDay = atLocalMidnight(
      chosen.getFullYear(),
      chosen.getMonth(),
      1
    );
  } else {
    const billY = billingStartFirstDay.getFullYear();
    const billM = billingStartFirstDay.getMonth();
    const monthFirst = atLocalMidnight(billY, billM, 1);

    const isSelectedMonthSameAsPurchaseMonth =
      billY === purchase.getFullYear() && billM === purchase.getMonth();

    periodStart = isSelectedMonthSameAsPurchaseMonth ? purchase : monthFirst;
    billingAnchorFirstDay = atLocalMidnight(billY, billM, 1);
  }

  let periodEndInclusive = getPaidPlanPeriodEndForBillingStart(periodStart);
  if (periodEndCapInclusive) {
    const cap = atLocalMidnight(
      periodEndCapInclusive.getFullYear(),
      periodEndCapInclusive.getMonth(),
      periodEndCapInclusive.getDate()
    );
    periodEndInclusive = minDateOnly(periodEndInclusive, cap);
  }

  if (periodStart.getTime() > periodEndInclusive.getTime()) {
    return {
      purchaseDate: purchase,
      periodStart,
      periodEndInclusive,
      billedPeriodEndInclusive: periodEndInclusive,
      lines: [],
      totalWon: 0,
      billingAnchorFirstDay,
    };
  }

  const lines: PaidPlanMonthLine[] = [];
  let maxBilledDay: Date | null = null;
  let y = periodStart.getFullYear();
  let m = periodStart.getMonth();

  while (
    y < periodEndInclusive.getFullYear() ||
    (y === periodEndInclusive.getFullYear() && m <= periodEndInclusive.getMonth())
  ) {
    const monthStart = atLocalMidnight(y, m, 1);
    const monthEnd = atLocalMidnight(y, m + 1, 0);
    const daysInMonth = monthEnd.getDate();

    const segStart = maxDateOnly(periodStart, monthStart);
    const segEnd = minDateOnly(periodEndInclusive, monthEnd);

    if (segStart.getTime() <= segEnd.getTime()) {
      const daysBilled = daysInclusiveDateOnly(segStart, segEnd);
      const isFullCalendarMonth =
        daysBilled === daysInMonth &&
        segStart.getFullYear() === y &&
        segStart.getMonth() === m &&
        segStart.getDate() === 1 &&
        segEnd.getFullYear() === monthEnd.getFullYear() &&
        segEnd.getMonth() === monthEnd.getMonth() &&
        segEnd.getDate() === monthEnd.getDate();

      const amountWon = isFullCalendarMonth
        ? monthlyWon
        : Math.min(daysBilled * partialDayRateWon, monthlyWon);

      lines.push({
        label: monthLabel(y, m),
        daysBilled,
        daysInMonth,
        amountWon,
      });
      if (!maxBilledDay || segEnd.getTime() > maxBilledDay.getTime()) {
        maxBilledDay = segEnd;
      }
    }

    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }

  const totalWon = lines.reduce((s, row) => s + row.amountWon, 0);
  const billedPeriodEndInclusive = maxBilledDay ?? periodEndInclusive;

  return {
    purchaseDate: purchase,
    periodStart,
    periodEndInclusive,
    billedPeriodEndInclusive,
    lines,
    totalWon,
    billingAnchorFirstDay,
  };
}

/** 날짜만 +n일 (로컬 자정 기준) */
function addDaysDateOnly(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  x.setDate(x.getDate() + n);
  return x;
}

function mergeCoverageIntervals(intervals: { start: Date; end: Date }[]): { start: Date; end: Date }[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start.getTime() - b.start.getTime());
  const out: { start: Date; end: Date }[] = [];
  let cur = {
    start: atLocalMidnight(
      sorted[0]!.start.getFullYear(),
      sorted[0]!.start.getMonth(),
      sorted[0]!.start.getDate()
    ),
    end: atLocalMidnight(sorted[0]!.end.getFullYear(), sorted[0]!.end.getMonth(), sorted[0]!.end.getDate()),
  };
  for (let i = 1; i < sorted.length; i += 1) {
    const n = sorted[i]!;
    const ns = atLocalMidnight(n.start.getFullYear(), n.start.getMonth(), n.start.getDate());
    const ne = atLocalMidnight(n.end.getFullYear(), n.end.getMonth(), n.end.getDate());
    const dayAfterCur = addDaysDateOnly(cur.end, 1);
    if (ns.getTime() <= dayAfterCur.getTime()) {
      if (ne.getTime() > cur.end.getTime()) cur.end = ne;
    } else {
      out.push(cur);
      cur = { start: ns, end: ne };
    }
  }
  out.push(cur);
  return out;
}

/** [rangeStart, rangeEnd] 날짜만 포함 구간에서 merged 제외한 조각들 */
function subtractMergedFromRange(
  rangeStart: Date,
  rangeEnd: Date,
  merged: { start: Date; end: Date }[]
): { start: Date; end: Date }[] {
  const rs = atLocalMidnight(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  const re = atLocalMidnight(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
  if (rs.getTime() > re.getTime()) return [];
  if (merged.length === 0) return [{ start: rs, end: re }];

  const parts: { start: Date; end: Date }[] = [];
  let cursor = rs;
  for (const ex of merged) {
    if (ex.end.getTime() < cursor.getTime()) continue;
    if (ex.start.getTime() > re.getTime()) break;
    if (cursor.getTime() < ex.start.getTime()) {
      const partEnd = minDateOnly(addDaysDateOnly(ex.start, -1), re);
      if (cursor.getTime() <= partEnd.getTime()) parts.push({ start: cursor, end: partEnd });
    }
    const next = addDaysDateOnly(ex.end, 1);
    cursor = maxDateOnly(next, cursor);
    if (cursor.getTime() > re.getTime()) return parts;
  }
  if (cursor.getTime() <= re.getTime()) parts.push({ start: cursor, end: re });
  return parts;
}

export type IndividualCoverageRange = { periodStart: Date; periodEnd: Date };

/**
 * `billingStartFirstDay`부터 산출한 청구 달력에서, **이미 다른 이용권이 덮는 날짜**는 제외하고 남은 일수만 과금.
 * `existingNonRefundedPasses`에는 미환불 **개별** 구간뿐 아니라, 이 계정에 적용 중인 **그룹·이벤트** 등 겹치면 안 되는 구간을 같은 형태로 넣을 수 있습니다.
 * 남는 일수가 없으면 `totalWon`은 0, `lines`는 빈 배열.
 */
export function computePaidPlanPricingExcludingIndividualCoverage(
  billingStartFirstDay: Date,
  purchaseDate: Date,
  existingNonRefundedPasses: IndividualCoverageRange[],
  monthlyWon: number = PAID_PLAN_MONTHLY_WON,
  partialDayRateWon: number = PAID_PLAN_PARTIAL_DAY_RATE_WON,
  periodEndCapInclusive?: Date | null
): PaidPlanPricingResult {
  const base = computePaidPlanPricing(
    billingStartFirstDay,
    purchaseDate,
    monthlyWon,
    partialDayRateWon,
    periodEndCapInclusive
  );
  const purchase = atLocalMidnight(
    purchaseDate.getFullYear(),
    purchaseDate.getMonth(),
    purchaseDate.getDate()
  );
  const windowStart = maxDateOnly(base.periodStart, purchase);
  const windowEnd = base.periodEndInclusive;

  if (windowStart.getTime() > windowEnd.getTime()) {
    return {
      ...base,
      periodStart: windowStart,
      periodEndInclusive: windowEnd,
      billedPeriodEndInclusive: base.billedPeriodEndInclusive,
      lines: [],
      totalWon: 0,
    };
  }

  const clipped: { start: Date; end: Date }[] = [];
  for (const p of existingNonRefundedPasses) {
    const ps = atLocalMidnight(p.periodStart.getFullYear(), p.periodStart.getMonth(), p.periodStart.getDate());
    const pe = atLocalMidnight(p.periodEnd.getFullYear(), p.periodEnd.getMonth(), p.periodEnd.getDate());
    const s = maxDateOnly(ps, windowStart);
    const e = minDateOnly(pe, windowEnd);
    if (s.getTime() <= e.getTime()) clipped.push({ start: s, end: e });
  }
  const mergedEx = mergeCoverageIntervals(clipped);

  const lines: PaidPlanMonthLine[] = [];
  let firstBilledDay: Date | null = null;
  let maxBilledDay: Date | null = null;

  let cy = base.periodStart.getFullYear();
  let cm = base.periodStart.getMonth();

  while (
    cy < base.periodEndInclusive.getFullYear() ||
    (cy === base.periodEndInclusive.getFullYear() && cm <= base.periodEndInclusive.getMonth())
  ) {
    const monthStart = atLocalMidnight(cy, cm, 1);
    const monthEnd = atLocalMidnight(cy, cm + 1, 0);
    const daysInMonth = monthEnd.getDate();

    const segStart = maxDateOnly(base.periodStart, monthStart);
    const segEnd = minDateOnly(base.periodEndInclusive, monthEnd);
    if (segStart.getTime() > segEnd.getTime()) {
      cm += 1;
      if (cm > 11) {
        cm = 0;
        cy += 1;
      }
      continue;
    }

    const winSegStart = maxDateOnly(segStart, windowStart);
    const winSegEnd = minDateOnly(segEnd, windowEnd);
    if (winSegStart.getTime() > winSegEnd.getTime()) {
      cm += 1;
      if (cm > 11) {
        cm = 0;
        cy += 1;
      }
      continue;
    }

    const uncovered = subtractMergedFromRange(winSegStart, winSegEnd, mergedEx);
    let billableDaysInMonth = 0;
    for (const u of uncovered) {
      billableDaysInMonth += daysInclusiveDateOnly(u.start, u.end);
    }

    if (billableDaysInMonth > 0) {
      for (const u of uncovered) {
        if (!firstBilledDay || u.start.getTime() < firstBilledDay.getTime()) firstBilledDay = u.start;
        if (!maxBilledDay || u.end.getTime() > maxBilledDay.getTime()) maxBilledDay = u.end;
      }

      const isFullCalendarMonth =
        billableDaysInMonth === daysInMonth &&
        winSegStart.getTime() === monthStart.getTime() &&
        winSegEnd.getTime() === monthEnd.getTime();

      const amountWon = isFullCalendarMonth
        ? monthlyWon
        : Math.min(billableDaysInMonth * partialDayRateWon, monthlyWon);

      lines.push({
        label: monthLabel(cy, cm),
        daysBilled: billableDaysInMonth,
        daysInMonth,
        amountWon,
      });
    }

    cm += 1;
    if (cm > 11) {
      cm = 0;
      cy += 1;
    }
  }

  const totalWon = lines.reduce((s, row) => s + row.amountWon, 0);

  const billingAnchorFirstDay = atLocalMidnight(
    billingStartFirstDay.getFullYear(),
    billingStartFirstDay.getMonth(),
    1
  );

  const billedPeriodEndInclusive = maxBilledDay ?? firstBilledDay ?? base.billedPeriodEndInclusive;

  return {
    purchaseDate: purchase,
    periodStart: firstBilledDay ?? base.periodStart,
    periodEndInclusive: base.periodEndInclusive,
    billedPeriodEndInclusive,
    lines,
    totalWon,
    billingAnchorFirstDay,
  };
}

export function formatKrw(n: number): string {
  return new Intl.NumberFormat('ko-KR').format(n);
}

function isPaidPlanFullMonthLine(row: PaidPlanMonthLine): boolean {
  return (
    row.daysBilled > 0 &&
    row.daysBilled === row.daysInMonth &&
    row.amountWon === PAID_PLAN_MONTHLY_WON
  );
}

/** 결제금액 산출 내역: 일할·월정액을 각각 한 줄로 요약 */
export function aggregatePaidPlanPricingSummaryLines(pricing: {
  lines: PaidPlanMonthLine[];
}): string[] {
  let partialDays = 0;
  let partialWon = 0;
  let fullMonths = 0;
  let fullMonthsWon = 0;
  for (const row of pricing.lines) {
    if (row.daysBilled <= 0 || row.amountWon <= 0) continue;
    if (isPaidPlanFullMonthLine(row)) {
      fullMonths += 1;
      fullMonthsWon += row.amountWon;
    } else {
      partialDays += row.daysBilled;
      partialWon += row.amountWon;
    }
  }
  const out: string[] = [];
  if (partialDays > 0) {
    const linear = partialDays * PAID_PLAN_PARTIAL_DAY_RATE_WON;
    if (partialWon === linear) {
      out.push(
        `${partialDays}일 : ${formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}원×${partialDays}일 = ${formatKrw(partialWon)}원`,
      );
    } else {
      out.push(
        `일할 청구 합계 ${formatKrw(partialWon)}원 (${partialDays}일 구간, 월 ${formatKrw(PAID_PLAN_MONTHLY_WON)}원 상한 반영)`,
      );
    }
  }
  if (fullMonths > 0) {
    out.push(
      `${fullMonths}개월 : ${formatKrw(PAID_PLAN_MONTHLY_WON)}원×${fullMonths}개월 = ${formatKrw(fullMonthsWon)}원`,
    );
  }
  return out;
}

export function formatDateKo(d: Date): string {
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** 이력·카드용 `2026.05.22` */
export function formatDateDot(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${mo}.${day}`;
}
